import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';

const ContactUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true,
    });
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: true,
        error: true,
        message: 'Please fill all required fields',
      });
      return;
    }

    try {
      const response = await fetch('https://apigrand.dodunsoftsolutions.com/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        setFormStatus({
          submitted: true,
          error: false,
          message: 'Thank you for your message! We will get back to you soon.',
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setFormStatus({ submitted: true, error: true, message: data.message });
      }
    } catch (err) {
      setFormStatus({
        submitted: true,
        error: true,
        message: 'Something went wrong. Please try again later.',
      });
    }
  };


  return (
    <div className="font-sans bg-white text-gray-800 min-h-screen">
      {/* Hero Section */}
      {/* Hero Section - Premium Style */}
      <div className="relative w-full overflow-hidden bg-gradient-to-br from-white via-gray-100 to-red-100  text-black">
        {/* Premium Decorative Circles */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-red-900 opacity-30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-red-700 opacity-20 rounded-full blur-2xl"></div>

        {/* Sparkle Overlay */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 z-0"></div>

        <div className="container mx-auto px-6 py-28 md:py-36 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            {/* Hero Content */}
            <motion.div
              className="md:w-1/2 text-center md:text-left"
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
                <span className="text-gray-900">Let’s </span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-yellow-500">
                  Connect
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 font-light mb-8 max-w-xl">
                Need help with bookings, show info, or partnerships? Reach out to our Grand Cinemaz support — where service meets luxury.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="#contact-form"
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition"
                >
                  Contact Us
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="#location"
                  className="px-8 py-3 border-2 border-red-500 text-red-600 hover:bg-red-50 rounded-lg font-medium transition"
                >
                  Our Location
                </motion.a>
              </div>
            </motion.div>

            {/* Hero Image with Floating Cards */}
            <motion.div
              className="md:w-1/2 relative"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="relative">
                {/* Main Card Image */}
                <motion.div
                  className="bg-white/10 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl transform rotate-1 overflow-hidden"
                  whileHover={{ rotate: 0, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                >
                  <img
                    src="https://ik.imagekit.io/zfayt6inj/grand.jpg?updatedAt=1746106570398"
                    alt="Grand Cinemaz Theatre"
                    className="rounded-xl object-cover shadow-inner"
                  />
                </motion.div>

                {/* Floating Contact Badge */}
                <motion.div
                  className="absolute -bottom-6 -left-6 bg-white text-gray-800 px-4 py-3 rounded-xl shadow-lg border border-gray-200"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                >
                  <p className="font-semibold text-sm">📞 Call Now: <span className="text-red-600">+91 62303 01476</span></p>
                </motion.div>

                {/* Floating Location Badge */}
                <motion.div
                  className="absolute -top-6 -right-6 bg-white text-gray-800 px-4 py-3 rounded-xl shadow-lg border border-gray-200"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 4, delay: 0.8 }}
                >
                  <p className="font-semibold text-sm">📍 Behdala, Himachal</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" className="text-white w-full">
            <path fill="currentColor" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,42.7C1120,32,1280,32,1360,32L1440,32L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"></path>
          </svg>
        </div>
      </div>


      {/* Contact Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="flex flex-col items-center mb-12" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Contact <span className="text-red-600">Us</span></h2>
          <div className="w-20 h-1 bg-red-600 mb-10"></div>
          <p className="text-lg text-gray-600 max-w-4xl text-center leading-relaxed">
            Have questions about movie schedules, special events, or venue bookings?
            Reach out to our friendly team at Grand Cinemaz and we'll be happy to assist you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <div data-aos="fade-up" id="contact-form">
            <motion.div
              className="bg-white rounded-xl shadow-lg border border-gray-100 p-8"
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-800">Send Us a Message</h3>

              {formStatus.submitted && (
                <div className={`mb-6 p-4 rounded-lg ${formStatus.error ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'}`}>
                  {formStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                    required
                  ></textarea>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition duration-300"
                >
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div data-aos="fade-up">
            <div className="space-y-8">
              {/* Address Card */}
              <motion.div
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-8"
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-red-100 rounded-lg text-red-600">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-2 text-gray-800">Our Location</h4>
                    <p className="text-gray-600">
                      8 Mile stone, Grand Cinemaz, Behdala, <br />
                      Himachal Pradesh 174306
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Phone Card */}
              <motion.div
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-8"
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-red-100 rounded-lg text-red-600">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-2 text-gray-800">Call Us</h4>
                    <p className="text-gray-600 mb-1">+91 62303 01476</p>
                    <p className="text-gray-600">+91 62302 95069</p>
                  </div>
                </div>
              </motion.div>

              {/* Email Card */}
              <motion.div
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-8"
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-red-100 rounded-lg text-red-600">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-2 text-gray-800">Email Us</h4>
                    <p className="text-gray-600"> grandcinemazuna@gmail.com</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mb-16" data-aos="fade-up" id="location">
          <h3 className="text-2xl font-bold mb-6 text-center text-gray-800">Find <span className="text-red-600">Us</span></h3>
          <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
            <div className="relative w-full h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3403.3390493319334!2d76.26982937457834!3d31.462875574387286!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391adb462546e94d%3A0x9cc1304239256f98!2sGrand%20Cinemaz!5e0!3m2!1sen!2sin!4v1652345678901!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Grand Cinemaz Location"
                className="absolute inset-0"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;